/* v3.18-1291 — EmailComposeDialog now wraps ComposeAndApprove.
 *
 * External API (open / onOpenChange / clientId / clientEmail /
 * clientName / dealId / dealNumber / signingToken / onEmailSent) is
 * unchanged so every caller keeps working.  Internally opens the
 * universal ComposeAndApprove dialog pre-filled from
 * ``/clients/{id}/email-recipients`` and sends through the
 * specialized ``/email/send`` endpoint (via onCustomSend) so the
 * existing audit / template-usage instrumentation continues to fire.
 */
import { useEffect, useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import ComposeAndApprove from './ComposeAndApprove';

const EmailComposeDialog = ({
  open,
  onOpenChange,
  clientId,
  clientEmail,
  clientName,
  dealId = null,
  dealNumber = null,
  signingToken = null,
  onEmailSent = null,
}) => {
  const [initial, setInitial] = useState(null);

  useEffect(() => {
    if (!open) { setInitial(null); return; }
    (async () => {
      // Pull deal-level recipient list (rep / manager / director /
      // VO / concierge / EQF). Same policy as before, just plumbed
      // through the universal composer.
      let cc = [];
      try {
        if (clientId) {
          const r = await api.get(`/clients/${clientId}/email-recipients`);
          cc = Array.isArray(r.data?.cc) ? r.data.cc.filter(Boolean) : [];
        }
      } catch { /* fall through — backend also auto-augments */ }

      setInitial({
        template_key: 'blank',
        subject:      '',
        body:         '',
        to:           clientEmail ? [clientEmail] : [],
        cc,
        client_id:    clientId,
        context:      { client_first_name: (clientName || '').split(' ')[0] || 'there' },
      });
    })();
  }, [open, clientId, clientEmail, clientName]);

  if (!open || !initial) return null;

  return (
    <ComposeAndApprove
      open={!!open}
      onOpenChange={(v) => { if (!v) onOpenChange?.(false); }}
      initial={initial}
      onCustomSend={async (payload) => {
        try {
          await api.post('/email/send', {
            client_id:    clientId,
            to_email:     (payload.to || [])[0] || clientEmail,
            cc:           (payload.cc || []).join(', ') || undefined,
            subject:      payload.subject,
            message:      payload.body,
            template:     'custom',
            deal_id:      dealId,
            deal_number:  dealNumber,
            signing_token: signingToken,
          });
          toast.success(`Email sent to ${(payload.to || [])[0] || clientEmail}`);
          onEmailSent?.();
          return { ok: true };
        } catch (e) {
          toast.error(e?.response?.data?.detail || 'Failed to send email');
          return { __cancel: true };
        }
      }}
    />
  );
};

export default EmailComposeDialog;
